import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, InputBase, IconButton, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Shared Billing Admin org selector: a D6 search box over a scrollable name list.
 * Extracted from the previously-duplicated copies in EntitlementsTab.js and the
 * Subscription Overview tab. The Customer Management tab keeps its own table for
 * now (different layout needs) — future follow-up to converge that too.
 *
 * Props:
 *  - orgs: full org list
 *  - loading: show a spinner while orgs load
 *  - selectedOrg: currently-selected org (highlights the row)
 *  - onSelect(org): row click handler
 *  - searchPlaceholder: search box placeholder
 *  - renderSubtitle(org): optional secondary line under each org name
 *  - maxHeight: list max height (default 320)
 */
export const filterOrgs = (orgs, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return orgs;
  return (orgs || []).filter((org) => {
    const name = (org.organization_name || '').toLowerCase();
    const id = (org.id || '').toLowerCase();
    const stripe = (org.stripe_customer_id || '').toLowerCase();
    return name.includes(q) || id.includes(q) || stripe.includes(q);
  });
};

export default function OrgSelector({
  orgs = [],
  loading = false,
  selectedOrg = null,
  onSelect,
  searchPlaceholder = 'Search...',
  renderSubtitle,
  maxHeight = 320,
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => filterOrgs(orgs, query), [orgs, query]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '0 8px',
          width: '180px',
          height: '36px',
          transition: 'width 0.3s ease',
          '& .MuiInputBase-root': { width: '100%' },
          '&:focus-within': { width: '300px', borderColor: '#1D3DFC' },
        }}
      >
        <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
        <InputBase
          placeholder={searchPlaceholder}
          inputProps={{ 'aria-label': 'search' }}
          sx={{
            fontSize: '0.875rem',
            padding: '2px 4px',
            flex: 1,
            '& input': { '&::placeholder': { color: '#9CA3AF' } },
          }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <IconButton
            size="small"
            onClick={() => setQuery('')}
            sx={{
              padding: '2px',
              color: '#6B7280',
              '&:hover': { backgroundColor: 'transparent', color: '#1D3DFC' },
            }}
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      <Paper variant="outlined" sx={{ mt: 1.5, maxHeight, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {query ? 'No organizations match your search.' : 'No organizations found.'}
          </Typography>
        ) : (
          filtered.map((org) => {
            const subtitle = renderSubtitle ? renderSubtitle(org) : null;
            const isSelected = selectedOrg?.id === org.id;
            return (
              <Box
                key={org.id}
                onClick={() => onSelect && onSelect(org)}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                  {org.organization_name || 'Unnamed organization'}
                </Typography>
                {subtitle && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            );
          })
        )}
      </Paper>
    </Box>
  );
}
